import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Button, Notice } from '../components/ui.js';

/**
 * Как добавлять товар в один тап.
 *
 * Страница разделена по устройствам не для порядка, а по необходимости. Один
 * и тот же приём работает на них по-разному, и общая инструкция «перетащите
 * кнопку» бесполезна тому, кто пришёл с телефона: перетаскивать там нечего,
 * а в Chrome на Android закладку с кодом вообще нельзя нажать.
 *
 * Разделы идут по тому, насколько путь бесшовен на этом устройстве, а не
 * по тому, как нам удобнее рассказывать.
 *
 * Код и закладки, и Ярлыка собирается при сборке сайта из одного и того же
 * разбора. В разметку его не вшиваем: две копии однажды разойдутся, и человек
 * с телефона начнёт получать не то, что человек с компьютера.
 */

export function BookmarkletPage(): JSX.Element {
  const [ссылкаЗакладки, setСсылкаЗакладки] = useState<string | null>(null);
  const [кодЯрлыка, setКодЯрлыка] = useState<string | null>(null);
  const [скопировано, setСкопировано] = useState<string | null>(null);

  useEffect(() => {
    fetch('/bookmarklet.txt')
      .then((ответ) => (ответ.ok ? ответ.text() : Promise.reject(new Error('нет файла'))))
      .then((текст) => setСсылкаЗакладки(текст.trim()))
      .catch(() => undefined);

    fetch('/shortcut.js')
      .then((ответ) => (ответ.ok ? ответ.text() : Promise.reject(new Error('нет файла'))))
      .then((текст) => setКодЯрлыка(текст.trim()))
      .catch(() => undefined);
  }, []);

  async function скопировать(что: string, метка: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(что);
      setСкопировано(метка);
    } catch {
      // Буфер недоступен — не беда, но промолчать нельзя: человек нажал и ждёт.
      setСкопировано('не вышло');
    }
    setTimeout(() => setСкопировано(null), 2500);
  }

  return (
    <div className="page stack">
      <h1>Добавлять в один тап</h1>

      <p>
        Загадарь умеет собрать карточку прямо со страницы магазина: название, цену и фотографию.
        Это работает даже там, где магазин не пускает наш сервер, — потому что страницу читает
        ваш собственный браузер, тот самый, в котором вы её и открыли.
      </p>

      <Notice tone="info">Настроить нужно один раз. Дальше — одно касание на странице товара.</Notice>

      <h2>iPhone и iPad</h2>
      <p>
        Самый удобный способ на телефоне — Ярлык. Он вызывается прямо из меню «Поделиться» и,
        в отличие от обычного «Поделиться», видит страницу, а значит забирает и цену.
      </p>
      <ol>
        <li>Откройте приложение «Команды» и создайте новую команду.</li>
        <li>
          Добавьте действие <b>«Выполнить JavaScript на веб-странице»</b> и вставьте в него код
          по кнопке ниже.
        </li>
        <li>
          Следующим действием добавьте <b>«Открыть URL»</b>.
        </li>
        <li>
          В настройках команды включите <b>«Показать в меню „Поделиться“»</b> и назовите её
          «В вишлист».
        </li>
      </ol>
      {кодЯрлыка && (
        <p>
          <Button onClick={() => void скопировать(кодЯрлыка, 'ярлык')}>
            {скопировано === 'ярлык' ? 'Скопировано' : 'Скопировать код для Ярлыка'}
          </Button>
        </p>
      )}

      <h2>Android</h2>
      <p>
        В Chrome на Android закладку с кодом нажать нельзя — так устроен сам браузер, и обойти это
        со стороны сайта невозможно. Поэтому здесь два пути.
      </p>
      <ul>
        <li>
          <b>Firefox</b> поддерживает расширения. Поставьте наше — и на странице товара появится
          кнопка, так же полно, как на компьютере.
        </li>
        <li>
          <b>Любой браузер</b> — «Поделиться» и выбрать Загадарь. Название приедет сразу, цену
          придётся вписать: система передаёт приложению только ссылку и заголовок, саму страницу
          она не отдаёт никому.
        </li>
      </ul>

      <h2>Компьютер</h2>
      <p>Перетащите кнопку на панель закладок и нажимайте её на странице товара.</p>
      {ссылкаЗакладки && (
        <p>
          {/*
            Ссылка с javascript: — это и есть закладка. Перетаскивание работает
            только с настоящим элементом ссылки, поэтому здесь именно он.
            Нажатие на месте ничего не даст, о чём и написано рядом.
          */}
          <a
            className="button button--primary"
            href={ссылкаЗакладки}
            onClick={(event) => event.preventDefault()}
          >
            🎁 В вишлист
          </a>{' '}
          <span className="muted">перетащите на панель закладок</span>
        </p>
      )}
      <p className="muted">
        Панель закладок включается сочетанием Ctrl + Shift + B, на macOS — Cmd + Shift + B.
      </p>
      <p>
        Есть и расширение: кнопка появляется на странице товара сама. Пока ставится вручную — в
        Chrome через chrome://extensions и «Загрузить распакованное», в Firefox через
        about:debugging.
      </p>

      <h2>Что уходит с вашего устройства</h2>
      <p>
        Только то, что вы увидите в форме: название, цена, фотография и адрес страницы. Всё
        читается прямо у вас, никакой посторонний сервис ваших ссылок не видит. Пока вы не нажали
        «Добавить желание», не отправляется вообще ничего.
      </p>
    </div>
  );
}
